import { atom } from 'jotai';

import { AccountType } from '@/components/Modals/CreateAccount/types';
import { CreateAccountType } from '@/components/Modals/SignUp/types';

import type { AssetInfo, UpsellState, UserInfo } from '../upsell-types';

/**
 * Private upsell-modal state, internal to the Download Gateway.
 *
 * Multiple writers used to fan out into the same atom shape (each call site
 * rebuilt the payload by hand), so a missed field on any caller would
 * silently corrupt the upsell modal. `useAssetUpsellTrigger` is the only
 * writer now and `useUpsellModalState` is the only reader; Biome's
 * `noRestrictedImports` rule keeps the atom file from leaking out of
 * `Gateway/internal/`. When the broader Modal Navigator deepening lands,
 * this whole mailbox goes away in favour of the navigator's API.
 */

const defaultUpsellState: UpsellState = {
  userInfo: {
    remainingTokens: 0,
    isPremium: false,
    isBusiness: false,
  },
  plans: [],
  isOpen: false,
  hasNoDownloadsLeft: false,
  reason: undefined,
  conversionPoint: undefined,
  currentAccountType: AccountType.Free,
  currentCreateAccountType: CreateAccountType.Free,
};

export const upsellAtom = atom<UpsellState>(defaultUpsellState);

export const assetInfoAtom = atom(
  (get) => get(upsellAtom).assetInfo,
  (get, set, update: AssetInfo | undefined) => {
    const current = get(upsellAtom);
    set(upsellAtom, { ...current, assetInfo: update });
  }
);

export const userInfoAtom = atom(
  (get) => get(upsellAtom).userInfo,
  (get, set, update: Partial<UserInfo>) => {
    const current = get(upsellAtom);
    set(upsellAtom, {
      ...current,
      userInfo: { ...current.userInfo, ...update },
    });
  }
);

export const isUpsellOpenAtom = atom(
  (get) => get(upsellAtom).isOpen,
  (get, set, isOpen: boolean) => {
    const current = get(upsellAtom);
    set(upsellAtom, { ...current, isOpen });
  }
);

export const currentAccountTypeAtom = atom(
  (get) => get(upsellAtom).currentAccountType,
  (get, set, currentAccountType: AccountType) => {
    const current = get(upsellAtom);
    set(upsellAtom, { ...current, currentAccountType });
  }
);

export const currentCreateAccountTypeAtom = atom(
  (get) => get(upsellAtom).currentCreateAccountType,
  (get, set, currentCreateAccountType: CreateAccountType) => {
    const current = get(upsellAtom);
    set(upsellAtom, { ...current, currentCreateAccountType });
  }
);

export const hasNoDownloadsLeftAtom = atom(
  (get) => get(upsellAtom).hasNoDownloadsLeft,
  (get, set, hasNoDownloadsLeft: boolean) => {
    const current = get(upsellAtom);
    set(upsellAtom, { ...current, hasNoDownloadsLeft });
  }
);

export const reasonAtom = atom((get) => get(upsellAtom).reason);

export const compactAtom = atom((get) => get(upsellAtom).compact ?? false);

export const conversionPointAtom = atom(
  (get) => get(upsellAtom).conversionPoint
);
